import { useState, useEffect } from 'react';

// Function to generate a new Ed25519 keypair
async function generateKeyPair(): Promise<CryptoKeyPair> {
  try {
    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: 'Ed25519',
      },
      true, // extractable
      ['sign', 'verify'] // usages
    ) as CryptoKeyPair; // Explicit type cast to ensure it's treated as CryptoKeyPair
    
    return keyPair;
  } catch (error) {
    console.error('Error generating Ed25519 keypair:', error);
    throw new Error('Failed to generate keypair');
  }
}

// Function to export keys to store in localStorage
async function exportKeyToString(key: CryptoKey): Promise<string> {
  try {
    const exported = await window.crypto.subtle.exportKey('jwk', key);
    return JSON.stringify(exported);
  } catch (error) {
    console.error('Error exporting key:', error);
    throw new Error('Failed to export key');
  }
}

// Function to import keys from localStorage
async function importKeyFromString(keyStr: string, isPrivate: boolean): Promise<CryptoKey> {
  try {
    const keyData = JSON.parse(keyStr);
    return await window.crypto.subtle.importKey(
      'jwk',
      keyData,
      {
        name: 'Ed25519',
      },
      true,
      isPrivate ? ['sign'] : ['verify']
    );
  } catch (error) {
    console.error('Error importing key:', error);
    throw new Error('Failed to import key');
  }
}

// Sign a message using the private key
export async function signMessage(payload: string): Promise<string> {
  try {
    // Correctly retrieve the private key string from localStorage
    const privateKeyStr = localStorage.getItem('a2a_private_key'); 
    
    if (!privateKeyStr) {
      console.error('Error signing message: No private key found in localStorage.');
      // Consider throwing an error here for clearer failure indication,
      // but for now, maintaining previous behavior of returning empty string on some errors.
      // throw new Error('No private key available for signing.'); 
      return '';
    }
    
    const privateKey = await importKeyFromString(privateKeyStr, true);
    const encoder = new TextEncoder();
    const data = encoder.encode(payload);
    
    const signature = await window.crypto.subtle.sign(
      'Ed25519',
      privateKey,
      data
    );
    
    // Convert the signature to a base64 string
    return btoa(String.fromCharCode(...new Uint8Array(signature)));
  } catch (error) {
    console.error('Error signing message:', error);
    return ''; // Return empty string on other errors during signing
  }
}

// Browser-compatible base64 decoding function
function base64ToUint8Array(base64String: string): Uint8Array {
  try {
    // Try the standard browser API first
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch (e) {
    // Fallback for handling non-standard base64
    console.warn('Error decoding base64, using fallback method:', e);
    
    // Simple fallback that handles common base64 variants
    // This won't throw but may produce incorrect results for invalid base64
    const base64 = base64String.replace(/-/g, '+').replace(/_/g, '/');
    try {
      return base64ToUint8Array(base64); // Try again with fixed string
    } catch {
      // Return empty array as last resort
      console.error('Failed to decode base64 even with fallback');
      return new Uint8Array();
    }
  }
}

// Verify a message signature using the public key
export async function verifyMessage(
  payload: string,
  signature: string,
  keyId: string
): Promise<boolean> {
  try {
    if (!signature || !keyId) {
      return false;
    }

    // Lookup stored public JWK string
    const publicKeyStr = localStorage.getItem(`a2a_public_key_${keyId}`);
    if (!publicKeyStr) {
      console.warn(`Public key not found for keyId: ${keyId}`);
      return false;
    }

    // Import the JWK public key
    const publicKey = await importKeyFromString(publicKeyStr, false);

    // Prepare the data to verify
    const encoder = new TextEncoder();
    const data = encoder.encode(payload);

    // Decode the base64 signature with our browser-compatible function
    const binarySignature = base64ToUint8Array(signature);
    
    // Empty signature will fail verification
    if (binarySignature.length === 0) {
      return false;
    }

    // Use crypto.subtle for browser and Jest compatibility
    return await crypto.subtle.verify(
      'Ed25519',
      publicKey,
      binarySignature,
      data
    );
  } catch (error) {
    console.error('Error verifying message:', error);
    return false;
  }
}

// Get the local key ID (this is an identifier for our public key)
export function getLocalKeyId(): string {
  const keyId = localStorage.getItem('a2a_key_id');
  return keyId || '';
}

// Get the public key for a given key ID
export async function getPublicKey(keyId: string): Promise<string> {
  try {
    // In a real system, this might query an API or key server
    // For local testing, we'll assume the keyId is stored locally
    const publicKeyStr = localStorage.getItem(`a2a_public_key_${keyId}`);
    if (!publicKeyStr) {
      throw new Error(`Public key not found for keyId: ${keyId}`);
    }
    return publicKeyStr;
  } catch (error) {
    console.error(`Error getting public key for ${keyId}:`, error);
    return '';
  }
}

// Hook to manage keys in the application
export function useA2AKeys() {
  const [keyId, setKeyId] = useState<string>(() => localStorage.getItem('a2a_key_id') || ''); // Initialize from localStorage directly
  const [keysGenerated, setKeysGenerated] = useState<boolean>(!!localStorage.getItem('a2a_key_id')); // Check if key_id exists

  useEffect(() => {
    const initializeKeys = async () => {
      // Check if private key also exists, as key_id might exist without it if something went wrong.
      if (!localStorage.getItem('a2a_key_id') || !localStorage.getItem('a2a_private_key')) {
        console.log("useA2AKeys: Initializing new A2A key pair.");
        try {
          const keyPair = await generateKeyPair();
          
          const privateKeyStr = await exportKeyToString(keyPair.privateKey);
          const publicKeyStr = await exportKeyToString(keyPair.publicKey);
          
          const newKeyId = `key_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
          
          localStorage.setItem('a2a_private_key', privateKeyStr);
          localStorage.setItem(`a2a_public_key_${newKeyId}`, publicKeyStr); // Store public key against newKeyId
          localStorage.setItem('a2a_key_id', newKeyId); // Store the newKeyId
          
          setKeyId(newKeyId);
          setKeysGenerated(true);
          console.log("useA2AKeys: New A2A key pair initialized and stored.");
        } catch (error) {
          console.error('useA2AKeys: Failed to initialize keys:', error);
          setKeysGenerated(false); // Ensure keysGenerated is false on error
        }
      } else {
        // Keys already exist
        console.log("useA2AKeys: A2A keys already exist.");
        // Ensure state reflects existing keys if not already set by useState initializer
        if (!keyId) setKeyId(localStorage.getItem('a2a_key_id') || '');
        if (!keysGenerated) setKeysGenerated(true);
      }
    };
    
    initializeKeys();
  }, [keyId, keysGenerated]); // Added keyId and keysGenerated to ensure re-check if state is somehow reset
  
  return {
    keyId,
    keysGenerated,
    signMessage, // signMessage is an export, not directly from hook state but relies on localStorage setup by hook
    verifyMessage,
    getPublicKey
  };
}
